import { TenantHomeDashboardNavigationBar } from 'components/LayoutComponent/VerticalLayout/HeaderBar';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Grid,
  List,
  ListItem,
  ListItemButton,
  Popper,
  Skeleton,
  Fade,
} from '@mui/material';

import StarBorderOutlinedIcon from '@mui/icons-material/StarBorderOutlined';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import Divider from '@mui/material/Divider';
import { useGetMyPersonalization } from 'services/v1/Tenant/ConfigurationService';
import { RowenaContent } from 'pages/Tenant/AroDashboard/RowenaContent';
import { useEffect, useMemo, useState } from 'react';
import { AutoCompleteItem } from 'types/api/Common/AutoCompleteTypes';
import { MyViewDashboardItem } from 'types/api/Tenant/ConfigurationTypes';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import { getQuickAccessNavigateURL } from 'components/LayoutComponent/VerticalLayout/QuickAccess';
import { PAGE_REF_NAVIGATION } from 'utils/Animation';
import { useNavigate } from 'react-router-dom';
import { useDashboardNaviagationPrefetchHandler } from 'hooks/useDashboardNavigationPrefetchHandler';
import { RootState } from 'store';
import { useSelector } from 'react-redux';

const listContainerStyle = {
  backgroundColor: '#fff',
  minWidth: 150,
  height: '100%',
  overflowY: 'auto',
  my: 1,
  px: 2,
};

const menuItemStyle = {
  py: 0,
  px: 0.5,
  borderRadius: '4px',
  color: '#797979',
  transition: 'all 0.15s ease',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    outline: '1px solid #aeafb0',
  },
  '&:active': {
    backgroundColor: '#42BB9320',
  },
  '&.Mui-selected': {
    backgroundColor: '#3f50b514',
    '& .MuiTypography-root': { fontWeight: 700, color: '#3f50b5' },
    '&:hover': { backgroundColor: '#3f50b51a' },
  },
};

const menuItemTextStyle = { fontSize: 15, py: 0.5 };

export default function AdminTenantHomePage() {
  const { role } = useSelector((state: RootState) => state.auth);
  const { data: allMyViewsData, isLoading } = useGetMyPersonalization({ enabled: true });
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState<HTMLDivElement | null>(null);
  const [activeSolution, setActiveSolution] = useState<AutoCompleteItem | null>(null);
  const prefetchHandler = useDashboardNaviagationPrefetchHandler();
  const navigate = useNavigate();

  const mapSolutionWithDashboard = useMemo(() => {
    const map = new Map();
    if (allMyViewsData?.data) {
      allMyViewsData.data.solutions.forEach((solution) => {
        const dashboards = allMyViewsData.data.dashboards.filter((dsb) => dsb.solutionDefinition.id === solution.value);
        map.set(solution.value.toString(), dashboards);
      });
    }

    return map;
  }, [allMyViewsData?.data]);
  const myViewDashboardList: MyViewDashboardItem[] = useMemo(() => {
    if (activeSolution?.value) {
      return mapSolutionWithDashboard.get(activeSolution.value);
    }
    return [];
  }, [activeSolution, mapSolutionWithDashboard]);
  // Auto-select the first solution on load
  useEffect(() => {
    if (!activeSolution && allMyViewsData?.data.solutions.length) {
      setActiveSolution(allMyViewsData.data.solutions[0]);
    }
  }, [activeSolution, allMyViewsData?.data.solutions]);

  const handleOnSolutionSelect = (item: AutoCompleteItem, event: React.MouseEvent<HTMLDivElement>) => {
    setSubMenuAnchorEl(event.currentTarget);
    setActiveSolution(item);
  };

  const handleOnDashboardClick = (dashboardItem: MyViewDashboardItem) => {
    const myViewItem = dashboardItem.myViews[0];
    const navigateURL = getQuickAccessNavigateURL(myViewItem, true, PAGE_REF_NAVIGATION.LEFT, role);
    navigateURL && navigate(navigateURL);
  };

  const handleOnPrefetchMyViewItem = (dashboardItem: MyViewDashboardItem) => {
    dashboardItem.myViews.forEach((myViewItem, index) => {
      const nextMyViewItem = dashboardItem.myViews?.[index + 1];
      prefetchHandler(myViewItem, nextMyViewItem);
    });
  };

  return (
    <TenantHomeDashboardNavigationBar
      breadCrumbs={[]}
      containerSx={{ height: '100%', flex: 1, flexDirection: 'column', overflowY: 'auto', display: 'flex' }}
      rootContainerSx={{ height: '90vh' }}
    >
      <Grid container spacing={2} height='100%'>
        <Grid item xs={12}>
          <Paper
            variant='outlined'
            sx={{
              height: '100%',
              transition: 'box-shadow 0.2s ease',
              '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
            }}
          >
            <Box p={2}>
              <Stack direction='row' gap={1} mb={1} alignItems='center'>
                <StarBorderOutlinedIcon
                  fontSize='medium'
                  sx={{
                    color: '#42BB93',
                  }}
                />
                <Typography variant='body1' fontWeight='bold' color='text.primary'>
                  myView
                </Typography>
                <Box sx={{ ml: 'auto' }}>
                  <RowenaContent workFlowId='MY_VIEW' label='Rowena' tooltip='Open Rowena' />
                </Box>
              </Stack>
              <Divider sx={{ mb: 1 }} />
              <Stack height='100%'>
                {isLoading ? (
                  <Stack width={'20%'} sx={listContainerStyle}>
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Skeleton
                        key={i}
                        animation='wave'
                        variant='rounded'
                        height={32}
                        sx={{ mb: 1, borderRadius: '4px' }}
                      />
                    ))}
                  </Stack>
                ) : !allMyViewsData?.data.solutions.length ? (
                  <Stack alignItems='center' justifyContent='center' py={6} gap={1}>
                    <InboxOutlinedIcon sx={{ fontSize: 48, color: '#c0c0c0' }} />
                    <Typography variant='body2' color='text.secondary'>
                      No solutions configured
                    </Typography>
                  </Stack>
                ) : (
                  <Stack direction='row' gap={1} height='100%'>
                    <Stack width={'20%'} overflow='auto' height='100%'>
                      <List sx={listContainerStyle}>
                        {allMyViewsData?.data.solutions.map((solution) => (
                          <ListItem disablePadding key={solution.value} sx={{ mb: 1 }}>
                            <ListItemButton
                              selected={activeSolution?.value === solution.value}
                              onMouseEnter={(event) => {
                                handleOnSolutionSelect(solution, event);
                              }}
                              sx={{
                                ...menuItemStyle,
                                paddingLeft: `10px`,
                                marginBottom: 0,
                                paddingBottom: 0,
                              }}
                            >
                              <Typography variant='input-label' sx={menuItemTextStyle}>
                                {solution.label}
                              </Typography>
                              <KeyboardArrowRight
                                sx={{
                                  marginLeft: 'auto',
                                  color: activeSolution?.value === solution.value ? '#3f50b5' : 'inherit',
                                  transition: 'color 0.15s ease',
                                }}
                              />
                            </ListItemButton>
                          </ListItem>
                        ))}
                      </List>
                    </Stack>
                    <Divider orientation='vertical' variant='middle' flexItem />
                    <Popper
                      open={Boolean(subMenuAnchorEl)}
                      anchorEl={subMenuAnchorEl}
                      placement='auto-start'
                      transition
                      sx={{ marginLeft: '30px !important' }}
                      modifiers={[
                        {
                          name: 'offset',
                          options: {
                            offset: [-20, 0],
                          },
                        },
                        {
                          name: 'preventOverflow',
                          options: {
                            boundary: 'viewport',
                          },
                        },
                      ]}
                    >
                      {({ TransitionProps }) => (
                        <Fade {...TransitionProps} timeout={150}>
                          <List sx={listContainerStyle}>
                            {myViewDashboardList.length === 0 ? (
                              <Stack alignItems='center' justifyContent='center' py={4} gap={1} minWidth={200}>
                                <InboxOutlinedIcon sx={{ fontSize: 36, color: '#c0c0c0' }} />
                                <Typography variant='body2' color='text.secondary'>
                                  No dashboards in this solution
                                </Typography>
                              </Stack>
                            ) : (
                              myViewDashboardList.map((dashboardItem) => (
                                <ListItem disablePadding key={dashboardItem.id}>
                                  <ListItemButton
                                    onClick={() => {
                                      handleOnDashboardClick(dashboardItem);
                                    }}
                                    onMouseEnter={() => handleOnPrefetchMyViewItem(dashboardItem)}
                                    sx={{
                                      ...menuItemStyle,
                                      paddingLeft: `10px`,
                                      marginBottom: 0,
                                      paddingBottom: 0,
                                    }}
                                  >
                                    <Typography variant='input-label' sx={menuItemTextStyle}>
                                      {dashboardItem.dashboardName}
                                    </Typography>
                                  </ListItemButton>
                                </ListItem>
                              ))
                            )}
                          </List>
                        </Fade>
                      )}
                    </Popper>
                  </Stack>
                )}
              </Stack>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </TenantHomeDashboardNavigationBar>
  );
}
